import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Page,
  Card,
  PageActions,
  Text,
  Loading,
  Checkbox,
  FormLayout,
  TextField,
  InlineError,
  Toast,
  Select,
  Modal,
  DropZone,
  Thumbnail,
} from "@shopify/polaris";
import { NoteIcon } from "@shopify/polaris-icons";
import axios from "../../Assets/Lib/axios";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import Select2 from "react-select";
import LoadingScreen from "../../Components/LoadingScreen";

const AddEditOnDemandVideo = (props) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isSaving, setIsSaving] = useState(
    props.type === "edit" ? true : false,
  );

  const [optionsElements, setOptionsElements] = useState([]);
  const [optionsStudios, setOptionsStudios] = useState([]);
  const [optionsSubtudios, setOptionsSubtudios] = useState([]);

  // Track original keys loaded on edit (to skip unchanged fields)
  const originalVideoKey = useRef("");
  const originalImageKey = useRef("");

  // --- Thumbnail image upload (same S3 flow as other pages) ---
  const imageKey = useRef("");
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState("");
  const [imageError, setImageError] = useState("");
  const [filesImage, setFilesImage] = useState([]);
  const validImageTypes = ["image/gif", "image/jpeg", "image/png"];

  const handleDropZoneImage = useCallback(
    (_dropFiles, acceptedFiles, _rejectedFiles) =>
      setFilesImage((prev) => [...prev, ...acceptedFiles]),
    [],
  );
  const fileUploadImage = !filesImage.length && (
    <DropZone.FileUpload actionHint="Accepts jpegs and pngs" />
  );
  const uploadedFilesImage = filesImage.length > 0 && (
    <div style={{ padding: "0" }}>
      <div className="rowDiv">
        <Thumbnail
          size="small"
          source={
            validImageTypes.includes(filesImage[filesImage.length - 1].type)
              ? window.URL.createObjectURL(filesImage[filesImage.length - 1])
              : NoteIcon
          }
        />
        <div>
          {filesImage[filesImage.length - 1].name}{" "}
          <Text variant="bodySm" as="p">
            {filesImage[filesImage.length - 1].size} bytes
          </Text>
        </div>
      </div>
    </div>
  );
  const sendImage = (files) => {
    setIsImageUploading(true);
    const form_data = new FormData();
    form_data.append("entity", "on_demand_video");
    form_data.append("image", files[files.length - 1]);
    axios
      .post(`/s3/save-file`, form_data)
      .then((result) => {
        imageKey.current = result.data.data;
        setIsImageUploading(false);
        setImagePreview(
          validImageTypes.includes(files[files.length - 1].type)
            ? window.URL.createObjectURL(files[files.length - 1])
            : "",
        );
        setImageError("");
      })
      .catch(() => setIsImageUploading(false));
  };

  // --- Video upload (signed-URL flow) ---
  const videoKey = useRef("");
  const [isVideoUploading, setIsVideoUploading] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoError, setVideoError] = useState("");
  const [videoPreview, setVideoPreview] = useState("");
  const [filesVideo, setFilesVideo] = useState([]);

  const handleDropZoneVideo = useCallback(
    (_dropFiles, acceptedFiles, _rejectedFiles) =>
      setFilesVideo((prev) => [...prev, ...acceptedFiles]),
    [],
  );
  const fileUploadVideo = !filesVideo.length && (
    <DropZone.FileUpload actionHint="Accepts mp4, mov, webm" />
  );
  const uploadedFilesVideo = filesVideo.length > 0 && (
    <div style={{ padding: "0" }}>
      <div className="rowDiv">
        <Thumbnail size="small" source={NoteIcon} />
        <div>
          {filesVideo[filesVideo.length - 1].name}{" "}
          <Text variant="bodySm" as="p">
            {(filesVideo[filesVideo.length - 1].size / (1024 * 1024)).toFixed(
              2,
            )}{" "}
            MB
          </Text>
        </div>
      </div>
    </div>
  );
  const sendVideo = async (files) => {
    const file = files[files.length - 1];
    setIsVideoUploading(true);
    setVideoProgress(0);
    setVideoError("");
    try {
      // Step 1: get signed upload URL
      const res = await axios.post(`/on-demand-videos/upload-url`, {
        filename: file.name,
        size: file.size,
      });
      const { upload_url, headers, key } = res.data.data;

      // Step 2: PUT the video file to S3 using the signed URL
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", upload_url);

        // Set all headers from the signed-URL response
        if (headers) {
          Object.entries(headers).forEach(([h, v]) => {
            xhr.setRequestHeader(h, Array.isArray(v) ? v[0] : v);
          });
        }

        // Set Content-Type matching the actual file
        xhr.setRequestHeader("Content-Type", file.type || "video/mp4");

        // Track upload progress
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setVideoProgress(Math.round((e.loaded / e.total) * 100));
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error("Network error during upload"));

        // Send the raw file as the request body
        xhr.send(file);
      });

      // Step 3: store the S3 key
      videoKey.current = key;
      setVideoPreview(file.name);
      setVideoProgress(100);
      setIsVideoUploading(false);
    } catch (err) {
      console.log(err);
      setVideoError("Video upload failed. Please try again.");
      setIsVideoUploading(false);
    }
  };

  const [item, setItem] = useState({
    title: "",
    description: "",
    duration: "",
    price: "",
    url: "",
    validity: "",
    validityType: "",
    isActive: true,
    parent: "",
    element: "",
    studio: "",
    substudio: "",
  });

  const handleChangeTitle = (newValue) => {
    setItem({ ...item, title: newValue });
    setTitleError("");
  };
  const [titleError, setTitleError] = useState("");

  const handleChangeDescription = (newValue) => {
    setItem({ ...item, description: newValue });
  };

  const handleChangeDuration = (newValue) => {
    setItem({ ...item, duration: newValue });
    setDurationError("");
  };
  const [durationError, setDurationError] = useState("");

  const handleChangePrice = (newValue) => {
    setItem({ ...item, price: newValue });
    setPriceError("");
  };
  const [priceError, setPriceError] = useState("");

  const handleChangeUrl = (newValue) => {
    setItem({ ...item, url: newValue });
    setUrlError("");
  };
  const [urlError, setUrlError] = useState("");

  const handleChangeValidity = (newValue) => {
    setItem({ ...item, validity: newValue });
  };

  const handleChangeValidityType = (newValue) => {
    setItem({ ...item, validityType: newValue });
  };

  const handleChangeActive = (newValue) => {
    setItem({ ...item, isActive: newValue });
  };

  const handleSelectChangeParent = (newValue) => {
    setItem({ ...item, parent: newValue });
    setParentError("");
  };
  const [parentError, setParentError] = useState("");

  const handleSelectChangeElement = (newValue) => {
    setItem({ ...item, element: newValue });
    setElementError("");
  };
  const [elementError, setElementError] = useState("");

  const handleSelectChangeStudio = (newValue) => {
    setItem({ ...item, studio: newValue });
    setStudioError("");
  };
  const [studioError, setStudioError] = useState("");

  const handleSelectChangeSubtudio = (newValue) => {
    setItem({ ...item, substudio: newValue });
    setSubstudioError("");
  };
  const [substudioError, setSubstudioError] = useState("");
  const [apiError, setApiError] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    (item.parent === "studio" || item.parent === "substudio") &&
      item.element &&
      axios
        .get(`elements/${item.element.value}/studios`)
        .then((result) => {
          setOptionsStudios(
            result.data.data.data.map((item) => {
              return {
                label: item?.name && item.name,
                value: String(item.id),
              };
            }),
          );
        })
        .catch((err) => console.log(err));
  }, [item?.element?.value]);

  useEffect(() => {
    item.parent === "substudio" &&
      item.element &&
      item.studio &&
      axios
        .get(
          `/elements/${item.element.value}/studios/${item.studio.value}/sub-studios`,
        )
        .then((result) => {
          setOptionsSubtudios(
            result.data.data.data.map((item) => {
              return {
                label: item?.name && item.name,
                value: String(item.id),
              };
            }),
          );
        })
        .catch((err) => console.log(err));
  }, [item?.studio?.value]);

  async function fetchData() {
    let responseElements = [];
    try {
      responseElements = await axios.get(`/elements`);
      setOptionsElements(
        responseElements.data.data.data.map((item) => {
          return {
            label: item.title,
            value: String(item.id),
          };
        }),
      );
    } catch (error) {
      console.log(error);
    }

    if (props.type === "edit") {
      try {
        const responseItem = await axios.get(`/on-demand-videos/${id}`);
        const data = responseItem.data.data;
        setItem({
          title: data.title || "",
          description: data.description || "",
          duration: data.duration ? String(data.duration) : "",
          price:
            data.price !== null && data.price !== undefined
              ? String(data.price)
              : "",
          url: data.url || "",
          image: data.image || "",
          validity:
            data.validity !== null && data.validity !== undefined
              ? String(data.validity)
              : "",
          validityType: data.validity_type || "",
          isActive: data.is_active ?? true,
          parent: data.parent_type || "",
          element: data.element
            ? { label: data.element.title, value: String(data.element.id) }
            : "",
          studio: data.studio
            ? { label: data.studio.name, value: String(data.studio.id) }
            : "",
          substudio: data.sub_studio
            ? { label: data.sub_studio.name, value: String(data.sub_studio.id) }
            : "",
        });
        if (data.url) {
          videoKey.current = data.url;
          originalVideoKey.current = data.url;
          setVideoPreview(data.url);
        }
        if (data.image) {
          imageKey.current = data.image;
          originalImageKey.current = data.image;
          setImagePreview(data.image_url || "");
        }
      } catch (error) {
        console.log(error);
      }
    }
    setIsSaving(false);
  }

  const [activeDelete, setActiveDelete] = useState(false);
  const handleChangeModalDelete = useCallback((activeDelete) => {
    setActiveDelete(!activeDelete);
    setIsSaving(false);
  }, []);
  const handleYesDelete = () => {
    setIsSaving(true);
    axios
      .delete(`/on-demand-videos/${id}`)
      .then((result) => {
        navigate("/admin/on-demand-videos");
      })
      .catch((err) => console.log(err));
  };

  const [active, setActive] = useState(false);
  const toggleActive = useCallback(() => setActive((active) => !active), []);
  const [toastContent, setToastContent] = useState("");
  const toastMarkup = active ? (
    <Toast content={toastContent} onDismiss={toggleActive} />
  ) : null;

  return (
    <Page
      title={props.title}
      backAction={{ onAction: () => navigate("/admin/on-demand-videos") }}
    >
      {isSaving ? <Loading /> : null}
      {isImageUploading || isVideoUploading ? <LoadingScreen /> : null}

      <Card sectioned>
        <FormLayout>
          <TextField
            value={item.title}
            onChange={handleChangeTitle}
            label="Title"
            error={titleError}
          />

          <Select
            label="Parent"
            options={[
              { label: "Element", value: "element" },
              { label: "Studio", value: "studio" },
              { label: "Substudio", value: "substudio" },
            ]}
            placeholder="Please select"
            onChange={handleSelectChangeParent}
            value={item.parent}
            error={parentError}
          />

          {item.parent === "element" ? (
            <FormLayout>
              <Text>Element</Text>
              <Select2
                options={optionsElements}
                onChange={handleSelectChangeElement}
                value={item.element}
                placeholder="Please select"
                styles={{
                  menu: (provided) => ({ ...provided, zIndex: 9999 }),
                }}
              />
              <InlineError message={elementError} />
            </FormLayout>
          ) : item.parent === "studio" ? (
            <FormLayout.Group>
              <FormLayout>
                <Text>Element</Text>
                <Select2
                  options={optionsElements}
                  onChange={handleSelectChangeElement}
                  value={item.element}
                  placeholder="Please select"
                  styles={{
                    menu: (provided) => ({ ...provided, zIndex: 9999 }),
                  }}
                />
                <InlineError message={elementError} />
              </FormLayout>
              <FormLayout>
                <Text>Studio</Text>
                <Select2
                  options={optionsStudios}
                  onChange={handleSelectChangeStudio}
                  value={item.studio}
                  placeholder="Please select"
                  styles={{
                    menu: (provided) => ({ ...provided, zIndex: 9999 }),
                  }}
                />
                <InlineError message={studioError} />
              </FormLayout>
            </FormLayout.Group>
          ) : item.parent === "substudio" ? (
            <FormLayout.Group>
              <FormLayout>
                <Text>Element</Text>
                <Select2
                  options={optionsElements}
                  onChange={handleSelectChangeElement}
                  value={item.element}
                  placeholder="Please select"
                  styles={{
                    menu: (provided) => ({ ...provided, zIndex: 9999 }),
                  }}
                />
                <InlineError message={elementError} />
              </FormLayout>
              <FormLayout>
                <Text>Studio</Text>
                <Select2
                  options={optionsStudios}
                  onChange={handleSelectChangeStudio}
                  value={item.studio}
                  placeholder="Please select"
                  styles={{
                    menu: (provided) => ({ ...provided, zIndex: 9999 }),
                  }}
                />
                <InlineError message={studioError} />
              </FormLayout>
              <FormLayout>
                <Text>Substudio</Text>
                <Select2
                  options={optionsSubtudios}
                  onChange={handleSelectChangeSubtudio}
                  value={item.substudio}
                  placeholder="Please select"
                  styles={{
                    menu: (provided) => ({ ...provided, zIndex: 9999 }),
                  }}
                />
                <InlineError message={substudioError} />
              </FormLayout>
            </FormLayout.Group>
          ) : null}

          <TextField
            value={item.description}
            onChange={handleChangeDescription}
            label="Description"
            multiline={4}
          />
          <Select
            label="Duration"
            options={[
              { label: "30 minutes", value: "30" },
              { label: "60 minutes", value: "60" },
            ]}
            onChange={handleChangeDuration}
            value={item.duration}
            placeholder="Please select"
            error={durationError}
          />
          <TextField
            value={item.price}
            onChange={handleChangePrice}
            label="Price"
            type="number"
            error={priceError}
          />

          <FormLayout.Group>
            <TextField
              value={item.validity}
              onChange={handleChangeValidity}
              label="Validity"
              type="number"
            />
            <Select
              label="Validity Type"
              options={[
                { label: "Hours", value: "hours" },
                { label: "Days", value: "days" },
                { label: "Months", value: "months" },
              ]}
              onChange={handleChangeValidityType}
              value={item.validityType}
              placeholder="Please select"
            />
          </FormLayout.Group>

          <Text>Video</Text>
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "row",
            }}
          >
            <div>
              <Thumbnail size="large" source={videoPreview ? NoteIcon : ""} />
            </div>
            <div
              style={{
                width: "100%",
                marginLeft: "10px",
              }}
            >
              <DropZone
                accept="video/mp4,video/quicktime,video/webm"
                onDrop={handleDropZoneVideo}
                allowMultiple={false}
                onDropAccepted={sendVideo}
                disabled={isVideoUploading}
              >
                {uploadedFilesVideo}
                {fileUploadVideo}
              </DropZone>
            </div>
          </div>
          {videoError && <InlineError message={videoError} />}

          <Text>Thumbnail Image</Text>
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "row",
            }}
          >
            <div>
              <Thumbnail
                size="large"
                source={imagePreview ? imagePreview : ""}
              />
            </div>
            <div
              style={{
                width: "100%",
                marginLeft: "10px",
              }}
            >
              <DropZone
                accept=".svg,.jpeg,.jpg,.png"
                onDrop={handleDropZoneImage}
                allowMultiple={false}
                onDropAccepted={sendImage}
                disabled={isImageUploading}
              >
                {uploadedFilesImage}
                {fileUploadImage}
              </DropZone>
            </div>
          </div>
          <InlineError message={imageError} />

          <Checkbox
            label="Is Active"
            checked={item.isActive}
            onChange={handleChangeActive}
          />
          <InlineError message={apiError} />
        </FormLayout>
      </Card>
      <PageActions
        primaryAction={{
          content: "Save",
          onClick: handleSave,
          loading: isSaving,
        }}
        secondaryActions={
          props.type === "edit"
            ? [
                {
                  content: "Delete",
                  destructive: true,
                  onClick: () => setActiveDelete(true),
                },
              ]
            : []
        }
      />
      {props.type === "edit" && (
        <Modal
          open={activeDelete}
          onClose={handleChangeModalDelete}
          title="Delete Video"
          primaryAction={{
            content: "Yes",
            onAction: handleYesDelete,
            loading: isSaving,
          }}
          secondaryActions={[
            {
              content: "No",
              loading: isSaving,
              onAction: handleChangeModalDelete,
            },
          ]}
        >
          <Modal.Section>
            <Text>
              <p>Are you sure you want to delete this video?</p>
            </Text>
          </Modal.Section>
        </Modal>
      )}
      {toastMarkup}
    </Page>
  );

  function handleSave() {
    if (
      item.title === "" ||
      item.duration === "" ||
      item.price === "" ||
      !videoKey.current ||
      item.parent === "" ||
      item.element === "" ||
      ((item.parent === "studio" || item.parent === "substudio") &&
        item.studio === "") ||
      (item.parent === "substudio" && item.substudio === "")
    ) {
      item.title === "" && setTitleError("Please enter a title");
      item.duration === "" && setDurationError("Please select a duration");
      item.price === "" && setPriceError("Please enter a price");
      !videoKey.current && setVideoError("Please upload a video");
      item.parent === "" && setParentError("Please select a parent");
      item.element === "" && setElementError("Please select an element");
      (item.parent === "studio" || item.parent === "substudio") &&
        item.studio === "" &&
        setStudioError("Please select a studio");
      item.parent === "substudio" &&
        item.substudio === "" &&
        setSubstudioError("Please select a substudio");
    } else {
      setApiError("");
      setIsSaving(true);
      const bodyObj = {
        title: item.title,
        description: item.description,
        duration: Number(item.duration),
        price: item.price,
        ...(props.type === "edit"
          ? {
              ...(videoKey.current !== originalVideoKey.current
                ? { url: videoKey.current }
                : {}),
              ...(imageKey.current !== originalImageKey.current
                ? { image: imageKey.current }
                : {}),
            }
          : {
              url: videoKey.current,
              ...(imageKey.current ? { image: imageKey.current } : {}),
            }),
        validity: item.validity ? Number(item.validity) : null,
        validity_type: item.validityType || null,
        is_active: item.isActive,
        parent_type: item.parent,
        element_id: item.element.value,
        ...(item.parent === "studio" || item.parent === "substudio"
          ? { studio_id: item.studio.value }
          : {}),
        ...(item.parent === "substudio"
          ? { sub_studio_id: item.substudio.value }
          : {}),
      };

      if (props.type === "edit") {
        axios
          .put(`/on-demand-videos/${id}`, bodyObj)
          .then((result) => {
            setToastContent("Changes have been saved successfully");
            toggleActive();
            setIsSaving(false);
          })
          .catch((error) => {
            setApiError(error?.response?.data?.message || "An error occurred");
            setIsSaving(false);
            console.log(error);
          });
      } else {
        axios
          .post(`/on-demand-videos`, bodyObj)
          .then((result) => {
            navigate("/admin/on-demand-videos");
          })
          .catch((error) => {
            setApiError(error?.response?.data?.message || "An error occurred");
            setIsSaving(false);
            console.log(error);
          });
      }
    }
  }
};

export default AddEditOnDemandVideo;
